import { Action } from 'redux';
import { Middleware } from '@reduxjs/toolkit';
import { MapState } from 'ff/debrief/models';

const version = '3.1';
const key = `debriefMapSettings`;

/**
 * Redux middleware that handles saving the Debrief Map Slice state to localStorage
 * When actions are dispatched we dispatch the action, and capture the state of the
 * store. The state is serialized to json along with a version string for future
 * retrieval.  Expectation is that on schema changes to the state that impact
 * user map settings, we would update the version string that would lazily invalidate
 * the stored state upon retrieval
 * @param store
 */
const settingPersistor: Middleware = store => next => action => {
    const result = next(action);
    try {
        if (
            store &&
            (action as Action).type.startsWith('MapSlice') &&
            store.getState().mapSlice
        ) {
            const persistedState = {
                version: version,
                state: store.getState().mapSlice,
            };
            const serialized = JSON.stringify(persistedState);
            window.localStorage.setItem(key, serialized);
        }
    } catch (err) {
        console.error('Failed to store settings state', err);
    }
    return result;
};

/**
 * Retrieve the state from the local storage based on the key. If the current version
 * differs from the stored version, the state is considered valid, so undefined
 * is returned instead
 */
const getStoredState = (): MapState | undefined => {
    const stored = window.localStorage.getItem(key);
    if (stored) {
        const parsed = JSON.parse(stored);
        if (parsed.state && parsed.version === version) {
            return parsed.state;
        }
    }
    return undefined;
};

export default {
    middleware: settingPersistor,
    getStoredSettings: getStoredState,
};
